import { Component } from 'react'
import { RouteComponentProps } from 'react-router-dom'
import DarkModeToggle from 'react-dark-mode-toggle'
import { appRef, Theme } from '../../app'
import { HiOutlineLogin } from 'react-icons/hi'
import LogoImg from '../../static/linetech_logo.png'
import { login, getSessionUser } from '../../api/auth'
import { toast } from 'react-toastify'
import { getString } from '../../locale'
import LocaleSelect from '../../components/localeselect'

type State = {
    working: boolean
    checking: boolean
    rememberMe: boolean
    theme: Theme
}

export class Login extends Component<RouteComponentProps, State> {
    state: State = {
        working: false,
        checking: true,
        rememberMe: true,
        theme: Theme.Light,
    }

    usernameRef: HTMLInputElement | null = null
    passwordRef: HTMLInputElement | null = null

    componentDidMount = async () => {
        appRef.setHeader(<HiOutlineLogin className="me-2" size={28} />, () => getString('pagelabels.login'))
        this.applyTheme(this.detectTheme())

        try {
            const result = await getSessionUser()
            if (result.data) {
                appRef.setUser(result.data)
                this.props.history.push('/')
                return
            }
        } catch (_err) {
            // No active session.
        }

        this.setState({ checking: false })
    }

    private detectTheme(): Theme {
        if (localStorage.theme === Theme.Dark || (!('theme' in localStorage) && window.matchMedia('(prefers-color-scheme: dark)').matches)) {
            return Theme.Dark
        }
        return Theme.Light
    }

    private applyTheme(theme: Theme) {
        localStorage.theme = theme
        if (theme === Theme.Dark) {
            document.documentElement.classList.add(Theme.Dark)
        } else {
            document.documentElement.classList.remove(Theme.Dark)
        }
        this.setState({ theme })
            ; (appRef as any).setState?.({ theme })
    }

    private getLoginErrorMessage(rawMessage: string): string {
        const msg = `${rawMessage || ''}`.trim()
        if (!msg) return getString('login.error.unexpected')
        if (msg === 'Invalid username') return getString('login.error.invalid_username')
        if (msg === 'Invalid password') return getString('login.error.invalid_password')
        if (msg === 'Wrong username/user number or password') return getString('login.error.wrong_credentials')
        if (msg === 'User is disabled') return getString('login.error.user_disabled')
        const secondsMatch = msg.match(/(\d+)\s*(?:second|seconds|second\(s\)|שניות)/i)
        const seconds = secondsMatch?.[1]
        if (msg.includes('Too many failed login attempts') || msg.includes('יותר מדי ניסיונות התחברות כושלים')) {
            return seconds ? getString('login.error.locked_seconds').replace('{0}', seconds) : getString('login.error.locked')
        }
        if (msg.includes('Rate limit exceeded') || msg.includes('הגעת למגבלת הבקשות')) {
            return seconds ? getString('login.error.rate_limited_seconds').replace('{0}', seconds) : getString('login.error.rate_limited')
        }
        if (msg.includes('Access denied')) return getString('login.error.access_denied')
        return msg
    }

    async handleSubmit(e: React.SyntheticEvent<HTMLFormElement>) {
        e.preventDefault()
        try {
            if (!this.usernameRef?.value) throw new Error(getString('login.error.invalid_username'))
            if (!this.passwordRef?.value) throw new Error(getString('login.error.invalid_password'))

            this.setState({ working: true })
            const result = await login(this.usernameRef.value, this.passwordRef.value, this.state.rememberMe)
            appRef.setUser(result.data)
            this.props.history.push('/')
        } catch (err) {
            this.setState({ working: false })
            toast.error(this.getLoginErrorMessage((err as any)?.message || ''))
        }
    }

    renderLoading() {
        const isDark = this.state.theme === Theme.Dark
        return (
            <div className={`min-h-screen flex justify-center items-center ${isDark ? 'bg-gray-900' : 'bg-gray-100'}`}>
                <div className={`text-center rounded-2xl p-10 shadow-xl border ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
                    <div className="animate-spin rounded-full h-14 w-14 border-b-4 border-blue-600 mx-auto"></div>
                    <p className={`mt-4 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>{getString('common.loading')}</p>
                </div>
            </div>
        )
    }

    render() {
        if (this.state.checking) return this.renderLoading()
        const isDark = this.state.theme === Theme.Dark

        return (
            <div className={`min-h-screen ${isDark ? 'bg-gray-900' : 'bg-gray-50'}`}>
                <div className={`min-h-screen grid grid-cols-1 lg:grid-cols-4 overflow-hidden ${isDark ? 'bg-gray-900' : 'bg-white'}`}>
                    <section className={`lg:col-span-1 p-6 sm:p-8 lg:p-10 border-b lg:border-b-0 lg:border-e flex flex-col ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
                        }`}>
                        <div className="flex items-center justify-between gap-3">
                            <LocaleSelect />
                            <DarkModeToggle
                                className="focus:outline-none ltr"
                                onChange={darkMode => this.applyTheme(darkMode ? Theme.Dark : Theme.Light)}
                                checked={this.state.theme === Theme.Dark}
                                size={68}
                            />
                        </div>

                        <div className="mt-10 text-center">
                            <div className="inline-flex rounded-lg px-3 py-2 bg-gray-100 border border-gray-200">
                                <img className="h-20 w-auto" src={LogoImg} alt="LineTech" />
                            </div>
                            <h2
                                style={{ fontFamily: "'Rubik', 'Assistant', 'Heebo', 'Segoe UI', sans-serif" }}
                                className={`mt-6 text-2xl font-extrabold tracking-tight ${isDark ? 'text-white' : 'text-gray-900'}`}
                            >
                                {getString('login.signin_message')}
                            </h2>
                        </div>

                        <form className="mt-8 space-y-4" method="POST" onSubmit={this.handleSubmit.bind(this)}>
                            <div>
                                <label htmlFor="username" className={`mb-1.5 block text-sm font-semibold ${isDark ? 'text-white' : 'text-gray-700'}`}>
                                    {getString('login.username')}
                                </label>
                                <input
                                    ref={ref => (this.usernameRef = ref)}
                                    id="username"
                                    name="username"
                                    autoComplete="username"
                                    required
                                    style={
                                        isDark
                                            ? ({ color: '#ffffff', WebkitTextFillColor: '#ffffff', caretColor: '#ffffff' } as React.CSSProperties)
                                            : undefined
                                    }
                                    className={`block w-full rounded-xl border px-4 py-3 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 ${isDark ? 'border-gray-600 bg-gray-700 text-white placeholder-gray-300' : 'border-gray-300 bg-white text-gray-900'
                                        }`}
                                    placeholder={getString('login.username')}
                                />
                            </div>

                            <div>
                                <label htmlFor="password" className={`mb-1.5 block text-sm font-semibold ${isDark ? 'text-white' : 'text-gray-700'}`}>
                                    {getString('login.password')}
                                </label>
                                <input
                                    ref={ref => (this.passwordRef = ref)}
                                    id="password"
                                    name="password"
                                    type="password"
                                    autoComplete="current-password"
                                    required
                                    style={
                                        isDark
                                            ? ({ color: '#ffffff', WebkitTextFillColor: '#ffffff', caretColor: '#ffffff' } as React.CSSProperties)
                                            : undefined
                                    }
                                    className={`block w-full rounded-xl border px-4 py-3 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 ${isDark ? 'border-gray-600 bg-gray-700 text-white placeholder-gray-300' : 'border-gray-300 bg-white text-gray-900'
                                        }`}
                                    placeholder={getString('login.password')}
                                />
                            </div>

                            <label className={`flex items-center gap-2 text-sm ${isDark ? 'text-white' : 'text-gray-700'}`}>
                                <input
                                    id="remember_me"
                                    name="remember_me"
                                    type="checkbox"
                                    checked={this.state.rememberMe}
                                    onChange={e => this.setState({ rememberMe: e.target.checked })}
                                    className={`h-4 w-4 rounded text-blue-600 focus:ring-blue-500 ${isDark ? 'border-gray-500' : 'border-gray-300'}`}
                                />
                                {getString('login.remember_me')}
                            </label>

                            <button
                                type="submit"
                                disabled={this.state.working}
                                className="w-full rounded-xl py-3 px-4 text-sm font-semibold text-white bg-blue-600 hover:bg-blue-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-60 disabled:cursor-not-allowed"
                            >
                                {this.state.working ? getString('common.loading') : getString('login.button')}
                            </button>
                        </form>

                        <div className="mt-auto pt-8 text-center">
                            <span
                                className={`inline-flex items-center rounded-md px-2.5 py-1 text-xs font-semibold tracking-wide ${isDark ? 'bg-black/70 text-white border border-gray-600' : 'bg-gray-100 text-gray-600 border border-gray-200'
                                    }`}
                            >
                                LineTech Control Center
                            </span>
                        </div>
                    </section>

                    <section
                        className={`hidden lg:block lg:col-span-3 relative overflow-hidden ${isDark ? 'bg-slate-950' : 'bg-slate-100'}`}
                    >
                        <div
                            className={`absolute inset-0 ${isDark
                                    ? 'bg-[radial-gradient(circle_at_18%_18%,rgba(56,189,248,0.22),transparent_28%),radial-gradient(circle_at_82%_16%,rgba(96,165,250,0.18),transparent_24%),radial-gradient(circle_at_72%_82%,rgba(45,212,191,0.18),transparent_30%),linear-gradient(135deg,#020617_0%,#0f172a_45%,#111827_100%)]'
                                    : 'bg-[radial-gradient(circle_at_18%_18%,rgba(14,165,233,0.12),transparent_30%),radial-gradient(circle_at_78%_14%,rgba(59,130,246,0.10),transparent_24%),radial-gradient(circle_at_76%_82%,rgba(20,184,166,0.12),transparent_28%),linear-gradient(135deg,#f8fbff_0%,#eef5fb_46%,#ddebf7_100%)]'
                                }`}
                        />
                        <div
                            className={`absolute inset-0 ${isDark ? 'opacity-40' : 'opacity-55'}`}
                            style={{
                                backgroundImage: isDark
                                    ? 'linear-gradient(rgba(148,163,184,0.08) 1px, transparent 1px), linear-gradient(90deg, rgba(148,163,184,0.08) 1px, transparent 1px)'
                                    : 'linear-gradient(rgba(51,65,85,0.06) 1px, transparent 1px), linear-gradient(90deg, rgba(51,65,85,0.06) 1px, transparent 1px)',
                                backgroundSize: '72px 72px',
                            }}
                        />
                        <div className={`absolute -top-24 left-[-8%] h-80 w-80 rounded-full blur-3xl ${isDark ? 'bg-cyan-400/10' : 'bg-sky-300/25'}`} />
                        <div className={`absolute bottom-[-12%] right-[-6%] h-96 w-96 rounded-full blur-3xl ${isDark ? 'bg-blue-500/10' : 'bg-cyan-200/35'}`} />
                        <svg viewBox="0 0 1200 900" className="absolute inset-0 h-full w-full" preserveAspectRatio="xMidYMid slice">
                            <defs>
                                <linearGradient id="heroFrame" x1="0" y1="0" x2="1" y2="1">
                                    <stop offset="0%" stopColor={isDark ? '#67e8f9' : '#0f766e'} />
                                    <stop offset="100%" stopColor={isDark ? '#2563eb' : '#2563eb'} />
                                </linearGradient>
                                <linearGradient id="heroPanel" x1="0" y1="0" x2="1" y2="1">
                                    <stop offset="0%" stopColor={isDark ? '#0f172a' : '#ffffff'} stopOpacity={isDark ? '0.96' : '0.98'} />
                                    <stop offset="100%" stopColor={isDark ? '#111827' : '#e2e8f0'} stopOpacity={isDark ? '0.82' : '0.96'} />
                                </linearGradient>
                                <linearGradient id="heroAccent" x1="0" y1="0" x2="1" y2="0">
                                    <stop offset="0%" stopColor={isDark ? '#22d3ee' : '#0891b2'} />
                                    <stop offset="50%" stopColor={isDark ? '#60a5fa' : '#2563eb'} />
                                    <stop offset="100%" stopColor={isDark ? '#2dd4bf' : '#0f766e'} />
                                </linearGradient>
                                <radialGradient id="heroGlow" cx="50%" cy="50%" r="50%">
                                    <stop offset="0%" stopColor={isDark ? '#38bdf8' : '#38bdf8'} stopOpacity={isDark ? '0.7' : '0.42'} />
                                    <stop offset="100%" stopColor={isDark ? '#38bdf8' : '#38bdf8'} stopOpacity="0" />
                                </radialGradient>
                                <filter id="heroShadow" x="-20%" y="-20%" width="140%" height="140%">
                                    <feDropShadow dx="0" dy="24" stdDeviation="24" floodColor={isDark ? '#020617' : '#94a3b8'} floodOpacity={isDark ? '0.45' : '0.18'} />
                                </filter>
                            </defs>

                            <circle cx="920" cy="170" r="170" fill="url(#heroGlow)" opacity={isDark ? '0.22' : '0.18'}>
                                <animateTransform attributeName="transform" type="translate" values="0 0; -14 10; 0 0" dur="18s" repeatCount="indefinite" />
                            </circle>
                            <circle cx="300" cy="730" r="220" fill="url(#heroGlow)" opacity={isDark ? '0.14' : '0.12'}>
                                <animateTransform attributeName="transform" type="translate" values="0 0; 16 -14; 0 0" dur="22s" repeatCount="indefinite" />
                            </circle>

                            <g opacity={isDark ? '0.42' : '0.3'} fill="none" stroke={isDark ? '#38bdf8' : '#0284c7'} strokeWidth="2">
                                <path d="M180 158 H 340 L 388 212" strokeDasharray="10 12">
                                    <animate attributeName="stroke-dashoffset" from="0" to="-220" dur="18s" repeatCount="indefinite" />
                                </path>
                                <path d="M210 688 H 320 L 392 646" strokeDasharray="10 12">
                                    <animate attributeName="stroke-dashoffset" from="0" to="220" dur="20s" repeatCount="indefinite" />
                                </path>
                                <path d="M816 362 H 930 L 930 296" strokeDasharray="8 10">
                                    <animate attributeName="stroke-dashoffset" from="0" to="-180" dur="15s" repeatCount="indefinite" />
                                </path>
                                <path d="M778 604 H 860 L 860 618" strokeDasharray="8 10">
                                    <animate attributeName="stroke-dashoffset" from="0" to="-140" dur="12s" repeatCount="indefinite" />
                                </path>
                            </g>

                            <g opacity="0.72" fill="none" stroke="url(#heroAccent)">
                                <path d="M-40 650 C 170 470, 330 770, 560 610 S 950 400, 1240 570" strokeWidth="3">
                                    <animate attributeName="stroke-dasharray" values="1 0; 1800 0; 1 0" dur="20s" repeatCount="indefinite" />
                                </path>
                                <path d="M-20 720 C 220 560, 360 830, 610 690 S 980 500, 1230 650" strokeWidth="1.8" opacity="0.55">
                                    <animate attributeName="stroke-dasharray" values="1 0; 1600 0; 1 0" dur="24s" repeatCount="indefinite" />
                                </path>
                                <path d="M90 235 C 270 110, 450 360, 675 248 S 1015 120, 1170 210" strokeWidth="1.8" opacity="0.4" strokeDasharray="8 16">
                                    <animate attributeName="stroke-dashoffset" from="0" to="-360" dur="14s" repeatCount="indefinite" />
                                </path>
                            </g>

                            <g opacity="0.8">
                                <circle cx="173" cy="585" r="6" fill={isDark ? '#67e8f9' : '#0891b2'}>
                                    <animate attributeName="r" values="5;7;5" dur="4.5s" repeatCount="indefinite" />
                                </circle>
                                <circle cx="425" cy="668" r="5" fill={isDark ? '#60a5fa' : '#2563eb'}>
                                    <animate attributeName="r" values="4;6;4" dur="5.2s" repeatCount="indefinite" />
                                </circle>
                                <circle cx="742" cy="554" r="6" fill={isDark ? '#2dd4bf' : '#0f766e'}>
                                    <animate attributeName="r" values="5;7;5" dur="4.8s" repeatCount="indefinite" />
                                </circle>
                                <circle cx="1034" cy="474" r="7" fill={isDark ? '#67e8f9' : '#0284c7'}>
                                    <animate attributeName="r" values="6;8;6" dur="5.8s" repeatCount="indefinite" />
                                </circle>
                            </g>

                            <g filter="url(#heroShadow)">
                                <path d="M252 212 L666 212 L816 322 L816 646 L402 646 L252 536 Z" fill="url(#heroPanel)" stroke="url(#heroFrame)" strokeOpacity="0.7" strokeWidth="2" />
                                <path d="M666 212 L816 322 L402 322 L252 212 Z" fill={isDark ? '#1e293b' : '#dbeafe'} fillOpacity={isDark ? '0.92' : '0.84'} stroke={isDark ? '#67e8f9' : '#0ea5e9'} strokeOpacity="0.3" />
                                <path d="M402 322 L816 322 L816 646 L402 646 Z" fill={isDark ? '#0b1220' : '#f8fafc'} fillOpacity={isDark ? '0.72' : '0.82'} />
                                <circle cx="286" cy="244" r="8" fill={isDark ? '#67e8f9' : '#0ea5e9'} opacity="0.85" />
                                <circle cx="778" cy="338" r="8" fill={isDark ? '#2dd4bf' : '#0f766e'} opacity="0.75" />
                                <circle cx="438" cy="612" r="8" fill={isDark ? '#60a5fa' : '#2563eb'} opacity="0.75" />
                            </g>

                            <g opacity={isDark ? '0.7' : '0.5'} fill="none" stroke={isDark ? '#1d4ed8' : '#93c5fd'} strokeWidth="1.2">
                                <path d="M436 352 H 780" />
                                <path d="M436 474 H 780" />
                                <path d="M608 352 V 620" />
                                <path d="M436 620 H 780" />
                            </g>

                            <g opacity="0.92">
                                <rect x="440" y="366" width="154" height="94" rx="18" fill={isDark ? '#0f172a' : '#f8fafc'} stroke={isDark ? '#38bdf8' : '#0284c7'} strokeOpacity="0.45" />
                                <rect x="622" y="366" width="154" height="94" rx="18" fill={isDark ? '#0f172a' : '#f8fafc'} stroke={isDark ? '#60a5fa' : '#2563eb'} strokeOpacity="0.45" />
                                <rect x="440" y="486" width="336" height="118" rx="22" fill={isDark ? '#0f172a' : '#f8fafc'} stroke={isDark ? '#2dd4bf' : '#0f766e'} strokeOpacity="0.34" />
                                <rect x="468" y="391" width="68" height="10" rx="5" fill={isDark ? '#38bdf8' : '#0284c7'} opacity="0.9" />
                                <rect x="468" y="409" width="98" height="8" rx="4" fill={isDark ? '#334155' : '#cbd5e1'} />
                                <rect x="468" y="425" width="76" height="8" rx="4" fill={isDark ? '#1e293b' : '#e2e8f0'} />
                                <rect x="650" y="391" width="56" height="10" rx="5" fill={isDark ? '#2dd4bf' : '#0f766e'} opacity="0.92" />
                                <rect x="650" y="409" width="96" height="8" rx="4" fill={isDark ? '#334155' : '#cbd5e1'} />
                                <rect x="650" y="425" width="72" height="8" rx="4" fill={isDark ? '#1e293b' : '#e2e8f0'} />
                                <path d="M475 565 C 522 520, 550 530, 596 500 S 682 454, 742 525" fill="none" stroke="url(#heroAccent)" strokeWidth="5" strokeLinecap="round" />
                                <circle cx="531" cy="532" r="7" fill={isDark ? '#67e8f9' : '#0891b2'} />
                                <circle cx="625" cy="497" r="7" fill={isDark ? '#60a5fa' : '#2563eb'} />
                                <circle cx="727" cy="519" r="7" fill={isDark ? '#2dd4bf' : '#0f766e'} />
                                <path d="M428 560 H 394 V 598" fill="none" stroke={isDark ? '#38bdf8' : '#0ea5e9'} strokeWidth="2.4" strokeDasharray="6 10">
                                    <animate attributeName="stroke-dashoffset" from="0" to="-64" dur="6s" repeatCount="indefinite" />
                                </path>
                                <path d="M790 546 H 846 V 604" fill="none" stroke={isDark ? '#2dd4bf' : '#0f766e'} strokeWidth="2.4" strokeDasharray="6 10">
                                    <animate attributeName="stroke-dashoffset" from="0" to="64" dur="7s" repeatCount="indefinite" />
                                </path>
                            </g>

                            <g>
                                <path d="M865 255 C 921 225, 982 238, 1011 286 C 1039 334, 1024 396, 974 428 C 923 460, 858 447, 827 397 C 797 348, 812 286, 865 255 Z" fill={isDark ? '#0b1220' : '#f8fafc'} stroke={isDark ? '#67e8f9' : '#0284c7'} strokeOpacity="0.55" strokeWidth="2" />
                                <circle cx="919" cy="341" r="48" fill="none" stroke={isDark ? '#22d3ee' : '#0891b2'} strokeWidth="3" opacity="0.9">
                                    <animateTransform attributeName="transform" type="rotate" from="0 919 341" to="360 919 341" dur="28s" repeatCount="indefinite" />
                                </circle>
                                <circle cx="919" cy="341" r="22" fill={isDark ? '#38bdf8' : '#7dd3fc'} fillOpacity={isDark ? '0.18' : '0.36'} stroke={isDark ? '#38bdf8' : '#0284c7'} strokeOpacity="0.85">
                                    <animate attributeName="r" values="20;24;20" dur="5s" repeatCount="indefinite" />
                                </circle>
                                <path d="M919 267 V 219" stroke={isDark ? '#67e8f9' : '#0891b2'} strokeWidth="3" strokeLinecap="round" />
                                <path d="M985 341 H 1033" stroke={isDark ? '#60a5fa' : '#2563eb'} strokeWidth="3" strokeLinecap="round" />
                                <path d="M919 415 V 463" stroke={isDark ? '#2dd4bf' : '#0f766e'} strokeWidth="3" strokeLinecap="round" />
                                <path d="M853 341 H 805" stroke={isDark ? '#67e8f9' : '#0891b2'} strokeWidth="3" strokeLinecap="round" />
                                <circle cx="919" cy="219" r="10" fill={isDark ? '#67e8f9' : '#0891b2'} />
                                <circle cx="1033" cy="341" r="10" fill={isDark ? '#60a5fa' : '#2563eb'} />
                                <circle cx="919" cy="463" r="10" fill={isDark ? '#2dd4bf' : '#0f766e'} />
                                <circle cx="805" cy="341" r="10" fill={isDark ? '#67e8f9' : '#0891b2'} />
                            </g>

                            <g opacity="0.9">
                                <animateTransform attributeName="transform" type="translate" values="0 0; 0 -8; 0 0" dur="8s" repeatCount="indefinite" />
                                <rect x="836" y="560" width="182" height="116" rx="24" fill={isDark ? '#0f172a' : '#ffffff'} stroke={isDark ? '#67e8f9' : '#0284c7'} strokeOpacity="0.36" />
                                <rect x="864" y="590" width="54" height="54" rx="16" fill={isDark ? '#082f49' : '#e0f2fe'} stroke={isDark ? '#38bdf8' : '#0ea5e9'} strokeOpacity="0.65" />
                                <path d="M887 626 L897 609 L907 626" fill="none" stroke={isDark ? '#67e8f9' : '#0891b2'} strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
                                <rect x="940" y="596" width="52" height="8" rx="4" fill={isDark ? '#60a5fa' : '#2563eb'} />
                                <rect x="940" y="615" width="38" height="8" rx="4" fill={isDark ? '#334155' : '#cbd5e1'} />
                                <rect x="940" y="633" width="58" height="8" rx="4" fill={isDark ? '#1e293b' : '#e2e8f0'} />
                            </g>

                            <g opacity={isDark ? '0.84' : '0.72'} fill="none" stroke={isDark ? '#38bdf8' : '#0284c7'}>
                                <path d="M155 802 C 285 738, 350 744, 458 768" strokeWidth="2.2" opacity="0.38" />
                                <path d="M820 174 C 866 148, 915 146, 982 168" strokeWidth="2" opacity="0.26" strokeDasharray="5 11">
                                    <animate attributeName="stroke-dashoffset" from="0" to="-120" dur="9s" repeatCount="indefinite" />
                                </path>
                            </g>
                        </svg>
                    </section>
                </div>
            </div>
        )
    }
}
